/** @jsxImportSource @emotion/react */
import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import CopyButton from "@/components/CopyButton/CopyButton";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import InputField from "@/components/InputField/InputField";
import TextLink from "@/components/TextLink/TextLink";
import { TEXT_LINK_SIZES } from "@/components/TextLink/types";
import {
  API_DOCS_URL,
  useStyles as sharedStyles,
  useMediaQueries
} from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useIntegrationsApiTableStore } from "@/stores/useTableStore";

interface GeneratedApiKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const GeneratedApiKeyDialog = ({
  isOpen,
  onClose
}: GeneratedApiKeyDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();

  const { body2Medium } = sharedStyles();

  const generatedApiKey = useIntegrationsDialogStore(
    state => state.generatedApiKey
  );
  const fetchData = useIntegrationsApiTableStore(state => state.fetchData);

  const keyValue = generatedApiKey ?? "";

  const handleClose = () => {
    fetchData(getData, handleResponse);
    onClose();
  };

  return (
    <Dialog
      data-testid="generated-api-key-dialog"
      title={t("Dialog##generate api key##title")}
      description={
        <span css={css(body2Medium)}>
          {t("Dialog##api management##description")}
          <TextLink
            size={TEXT_LINK_SIZES.Normal}
            href={API_DOCS_URL}
            target="_blank"
          >
            {t("General##here")}
          </TextLink>
          {"."}
        </span>
      }
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={handleClose}
      width={!toMd ? DialogWidth.MinWidth : undefined}
      actions={
        <DialogActions
          onClose={handleClose}
          isLoading={false}
          isreadyToConfirm={false}
          hideAcceptButton
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="generated-api-key-close-button"
        />
      }
    >
      <div>
        <BannerMessage
          testId="generated-api-key-success-banner"
          status={BANNER_MESSAGE_STATUSES.Success}
          title={t("Dialog##api management##success banner")}
        />
        <div
          css={css({
            display: "flex",
            alignItems: "flex-end",
            gap: "8px",
            margin: "16px 0 0"
          })}
        >
          <InputField
            data-testid="generated-api-key-value"
            size="medium"
            fullWidth
            readOnly
            value={keyValue}
            labelLeft={t("Dialog##api management##generated api key")}
          />
          <CopyButton
            testId="generated-api-key-copy-button"
            size="normal"
            copiedValue={keyValue}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default GeneratedApiKeyDialog;
